import { isEmpty } from 'lodash-es';
import { Observable, Subject, catchError, debounceTime, forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';

import { computed, inject } from '@angular/core';

import { UserPreferenceKey, UserPreferencesStore } from '@mitsubishi/core';

import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import {
  FilterModelDto,
  FilterValuesResponseDto,
  GoodForSelectionItemDto,
  SortModelItemDto,
  StudyCountersDto,
  StudyEventDto,
  StudyUnitDto,
  SubcategoryCounterDto,
  SubcategoryMetadataDto,
  UnitsQueryRequestDto,
  UnitsQueryResponseDto,
  UnitsWithPerformanceAccessoriesResponseDto,
} from '../models/study-next.model';
import { StudyQueryService } from '../services/study-query.service';
import { StudyService } from '../services/study.service';
import { mergeRefresh, planRefresh } from '../ws/study/study-event';
import { ProjectStudiesStore } from './project-studies.store';
import { StudyPerformanceFiltersStore } from './study-performance-filters.store';

/** Which slice of the study the grid is showing: the All-models tab, or one subcategory tab. */
export type StudyNextScope = { kind: 'all' } | { kind: 'subcategory'; uuid: string };

/** A scope's unit counts under the grid's own column filters, as `units/search` answered them. */
export type StudyNextFilteredCount = Pick<UnitsQueryResponseDto, 'filteredUnits' | 'totalUnits'>;

/**
 * The grid's id per scope, under which its column state and column filters are saved as a user
 * preference. Here rather than in the grid because the store reads the saved filters too.
 */
export const studyNextGridId = (scope: StudyNextScope): string =>
  scope.kind === 'all' ? 'study-next-grid-all' : `study-next-grid-${scope.uuid}`;

/** Key of `filteredCountByScope` and `metadataByScope`. */
const scopeKey = (scope: StudyNextScope): string => (scope.kind === 'all' ? 'all' : scope.uuid);

/** How long realtime events are left to settle before the page re-reads once for all of them (`PLAN.md` §6.11). */
export const STUDY_EVENT_COALESCE_MS = 100;

type StudyNextState = {
  projectId: string | null;
  studyId: string | null;
  counters: StudyCountersDto | null;
  goodForSelectionItems: GoodForSelectionItemDto[];
  scope: StudyNextScope;
  /**
   * The metadata of every scope read since the page loaded, by `scopeKey`. Per scope rather than the
   * active one alone: a tab visited before opens without asking again, and the grid of the tab being
   * left — alive until its body has slid out — keeps reading its own columns instead of rebuilding itself
   * from the next tab's. Copies a change may have outdated are dropped (`forgetMetadata`).
   */
  metadataByScope: Record<string, SubcategoryMetadataDto>;
  /** Mirrors of the grid's own state: AG Grid owns them, the store records what was queried with. */
  sortModel: SortModelItemDto[];
  filterModel: FilterModelDto;
  totalUnits: number;
  filteredUnits: number;
  /**
   * Per scope (`'all'` or the subcategory uuid), the counts under its column filters — only for scopes
   * that have some. The counters endpoint knows the persisted performance filters alone, so without this
   * a tab filtered on a column (Fans type, Model name…) fell back to its plain total as soon as the user
   * left it, and after a reload until it was opened again.
   */
  filteredCountByScope: Record<string, StudyNextFilteredCount>;
  /** The price type of the last page queried: what a `totalPrices` filter means in the background counts. */
  priceType: UnitsQueryRequestDto['priceType'] | null;
  version: string | null;
  isLoadingStudy: boolean;
  hasError: boolean;
  selectedUnitIds: string[];
};

const initialState: StudyNextState = {
  projectId: null,
  studyId: null,
  counters: null,
  goodForSelectionItems: [],
  // All models is the landing tab, as on the legacy page (selectedIndex = 2).
  scope: { kind: 'all' },
  metadataByScope: {},
  sortModel: [],
  filterModel: {},
  totalUnits: 0,
  filteredUnits: 0,
  filteredCountByScope: {},
  priceType: null,
  version: null,
  isLoadingStudy: false,
  hasError: false,
  selectedUnitIds: [],
};

/** What `loadStudy` reads in one round trip besides the detail; the scope's metadata comes separately. */
type StudyScaffold = Pick<StudyNextState, 'counters' | 'goodForSelectionItems'>;

const sameScope = (a: StudyNextScope, b: StudyNextScope): boolean =>
  a.kind === 'all' ? b.kind === 'all' : b.kind === 'subcategory' && a.uuid === b.uuid;

/**
 * State for the flag-gated new Study page. The grid runs on AG Grid's server-side row model, so
 * block loading is AG's business: the store owns the page's scaffold (counters, metadata, scope,
 * selection) and serves blocks of units to the grid's datasource through `queryPage`.
 *
 * The study's detail is not its own: `ProjectStudiesStore` holds it for every reader of the study
 * (PLAN.md P1), the route resolver loads it before this page renders, and the project's JSON patches
 * refresh it there. This store reads it and asks for a refresh when the rows need one.
 */
export const StudyNextStore = signalStore(
  withState(initialState),
  withComputed(({ studyId }, projectStudies = inject(ProjectStudiesStore)) => ({
    detail: computed(() => {
      const id = studyId();
      return id ? (projectStudies.detailById()[id] ?? null) : null;
    }),
  })),
  withComputed(({ detail, counters, scope, metadataByScope }) => ({
    subcategories: computed(() => detail()?.subcategories ?? []),
    /** The active scope's metadata, or null until it has been read. */
    metadata: computed(() => metadataByScope()[scopeKey(scope())] ?? null),
    /** The active subcategory, or null on the All-models scope. */
    subcategoryId: computed(() => {
      const current = scope();
      return current.kind === 'subcategory' ? current.uuid : null;
    }),
    /** Partial: a subcategory the detail has just gained has no counter until the counters land. */
    counterBySubcategory: computed(() => {
      const map: Partial<Record<string, SubcategoryCounterDto>> = {};
      for (const counter of counters()?.subcategories ?? []) {
        map[counter.subcategoryUuid] = counter;
      }
      return map;
    }),
  })),
  /**
   * Two events for the grid, its only subscriber. `rowsInvalidated$` fires once an `invalidateRows` has
   * landed everything a page is projected against — the subcategory list in `detail` and the scope's
   * `metadata` — so the grid re-queries against fresh data; or at once when only which units a page holds
   * changed (a performance filter), with nothing to land first. `unitsChanged$` names units another write
   * changed, for the grid to re-read in place if it shows them. Observables rather than counters in
   * state: they are events, and nothing derives from them.
   */
  withProps(() => {
    const rowsInvalidated = new Subject<void>();
    const unitsChanged = new Subject<string[]>();
    return {
      _rowsInvalidated: rowsInvalidated,
      rowsInvalidated$: rowsInvalidated.asObservable(),
      _unitsChanged: unitsChanged,
      unitsChanged$: unitsChanged.asObservable(),
    };
  }),
  withMethods(
    (
      store,
      service = inject(StudyQueryService),
      projectStudies = inject(ProjectStudiesStore),
      studyService = inject(StudyService),
      performanceFilters = inject(StudyPerformanceFiltersStore),
      userPreferences = inject(UserPreferencesStore)
    ) => {
      /**
       * The two ids every read here needs. `loadStudy` patches both before any of them can run, so the
       * empty-string fallback is unreachable — it exists to keep the reads free of null checks, not as a
       * guard.
       */
      const ids = (): { projectId: string; studyId: string } => ({
        projectId: store.projectId() ?? '',
        studyId: store.studyId() ?? '',
      });

      /**
       * One page, for AG Grid's server-side datasource. The scope is the caller's, not the store's:
       * a tab body is created without a guaranteed order against the tab-change handler, so a grid
       * reading `scope()` here could query the tab it was replacing. Sort and filter travel in the
       * request because AG Grid owns them (`params.request`); the store only records what the page was
       * queried with, so a later counters/metadata refetch can reuse it (PAGINATION.md §7).
       */
      const queryPage = (scope: StudyNextScope, request: UnitsQueryRequestDto): Observable<UnitsQueryResponseDto> => {
        const { projectId, studyId } = ids();
        const page$ =
          scope.kind === 'all'
            ? service.searchStudyUnits(projectId, studyId, request)
            : service.searchSubcategoryUnits(projectId, studyId, scope.uuid, request);

        return page$.pipe(
          tap({
            next: response => {
              // Keyed by the request's own scope, so a late answer lands where it belongs.
              recordFilteredCount(scope, request.filterModel, response);

              // A page of a tab no longer active is the grid's alone: its answer can land after the scope
              // moved — seen on dev with a sort active — and recording it would put that tab's count
              // under the new tab's label, `108/12` on a tab of 12 with no filter.
              if (!sameScope(scope, store.scope())) {
                return;
              }
              patchState(store, {
                sortModel: request.sortModel ?? [],
                filterModel: request.filterModel ?? {},
                totalUnits: response.totalUnits,
                filteredUnits: response.filteredUnits,
                priceType: request.priceType ?? null,
                version: response.version,
                hasError: false,
              });
            },
            error: () => patchState(store, { hasError: true }),
          })
        );
      };

      /** A scope queried without column filters leaves the map: its tab reads the counters again. */
      const recordFilteredCount = (
        scope: StudyNextScope,
        filterModel: FilterModelDto | undefined,
        count: StudyNextFilteredCount
      ): void => {
        const { [scopeKey(scope)]: _previous, ...others } = store.filteredCountByScope();
        patchState(store, {
          filteredCountByScope: isEmpty(filterModel)
            ? others
            : {
                ...others,
                [scopeKey(scope)]: { filteredUnits: count.filteredUnits, totalUnits: count.totalUnits },
              },
        });
      };

      /**
       * The counts of the tabs the user is not on, under the column filters saved for their grids. Only
       * the active tab's grid queries, so a filtered tab left behind — or never opened since the reload —
       * has nobody else to ask: one single-unit `units/search` per tab that has saved filters, and none
       * for the others. The active scope is skipped, its grid's own pages keep it current. A failed count
       * leaves the tab on the counters rather than raising the page's error banner.
       */
      const refreshFilteredCounts = rxMethod<void>(
        pipe(
          switchMap(() => {
            const { projectId, studyId } = ids();
            const scopes = [
              { kind: 'all' } as StudyNextScope,
              ...store
                .subcategories()
                .map((subcategory): StudyNextScope => ({ kind: 'subcategory', uuid: subcategory.uuid })),
            ].filter(scope => !sameScope(scope, store.scope()));

            const counts = scopes.map(scope => {
              const filterModel = userPreferences.read<{ filter?: { filterModel?: FilterModelDto } }>(
                UserPreferenceKey.GRID_STATE,
                studyNextGridId(scope)
              )?.filter?.filterModel;
              if (isEmpty(filterModel)) {
                return of({ scope, filterModel, count: null });
              }

              const request: UnitsQueryRequestDto = {
                startUnit: 0,
                unitCount: 1,
                filterModel,
                priceType: store.priceType() ?? undefined,
              };
              const count$ =
                scope.kind === 'all'
                  ? service.searchStudyUnits(projectId, studyId, request)
                  : service.searchSubcategoryUnits(projectId, studyId, scope.uuid, request);
              return count$.pipe(
                map(count => ({ scope, filterModel, count: count as StudyNextFilteredCount | null })),
                catchError(() => of({ scope, filterModel: undefined, count: null }))
              );
            });

            return (counts.length ? forkJoin(counts) : of([])).pipe(
              tap(results =>
                results.forEach(({ scope, filterModel, count }) =>
                  recordFilteredCount(
                    scope,
                    count ? filterModel : undefined,
                    count ?? { filteredUnits: 0, totalUnits: 0 }
                  )
                )
              )
            );
          })
        )
      );

      const fetchScaffold = (): Observable<StudyScaffold> => {
        const { projectId, studyId } = ids();

        return forkJoin([
          service.getStudyCounters(projectId, studyId),
          service.getStudyGoodForSelection(projectId, studyId),
        ]).pipe(
          map(([counters, goodForSelection]) => ({
            counters,
            goodForSelectionItems: goodForSelection.items,
          }))
        );
      };

      const fetchMetadata = (scope: StudyNextScope): Observable<SubcategoryMetadataDto> => {
        const { projectId, studyId } = ids();

        return scope.kind === 'all'
          ? service.getStudyMetadata(projectId, studyId)
          : service.getSubcategoryMetadata(projectId, studyId, scope.uuid);
      };

      /** A scope's metadata, active or not: each grid reads its own scope's, never the active one's. */
      const metadataOf = (scope: StudyNextScope): SubcategoryMetadataDto | null =>
        store.metadataByScope()[scopeKey(scope)] ?? null;

      /** Filed under the scope it was read for, whichever tab is active by the time it lands. */
      const setMetadata = (scope: StudyNextScope, metadata: SubcategoryMetadataDto): void =>
        patchState(store, { metadataByScope: { ...store.metadataByScope(), [scopeKey(scope)]: metadata } });

      /**
       * Drops held metadata a change may have outdated: the scopes named, or all of them. Never the active
       * scope's: its grid is built from it — without it the grid would lose its columns — and the re-read
       * that comes with a change to its rows replaces it.
       */
      const forgetMetadata = (keys?: string[]): void => {
        const active = scopeKey(store.scope());
        patchState(store, {
          metadataByScope: Object.fromEntries(
            Object.entries(store.metadataByScope()).filter(
              ([key]) => key === active || (keys !== undefined && !keys.includes(key))
            )
          ),
        });
      };

      const loadMetadata = rxMethod<StudyNextScope>(
        pipe(
          switchMap(scope =>
            fetchMetadata(scope).pipe(
              tapResponse({
                next: metadata => setMetadata(scope, metadata),
                error: () => patchState(store, { hasError: true }),
              })
            )
          )
        )
      );

      /**
       * The subcategory's persisted numeric filters, for the performance headers' funnel icon and dialog
       * prefill (`StudyPerformanceFiltersStore`, which those headers read). `units/search` applies them
       * server-side; this copy only paints. Loaded with the scope, re-read when another user changes them.
       * The filters store holds one subcategory at a time, so a response arriving after the tab is gone
       * must not overwrite the next tab's entry — hence the guard. A failed read is reported like every
       * other read of this store, through `hasError`.
       */
      const loadPerformanceFilters = rxMethod<string>(
        pipe(
          switchMap(subcategoryId => {
            const { projectId, studyId } = ids();

            return studyService.getPerformanceFilters(projectId, studyId, subcategoryId).pipe(
              tapResponse({
                next: filters => {
                  if (store.subcategoryId() === subcategoryId) {
                    performanceFilters.setSubcategoryAppliedPerformanceFilters(subcategoryId, filters);
                  }
                },
                error: () => patchState(store, { hasError: true }),
              })
            );
          })
        )
      );

      /**
       * A scope change does not fetch a page: each tab body owns its own grid instance
       * (`matTabContent`), so the new grid's datasource issues the first request itself. `filteredUnits`
       * and `totalUnits` are the last page's counts for the scope that was queried, and the new tab's
       * label reads them — so they are cleared here and the label falls back to the counters until the
       * new grid's first page lands. A tab visited before still holds its metadata, so its grid is built
       * at once: only a scope never read asks for it.
       */
      const selectScope = (scope: StudyNextScope): void => {
        patchState(store, {
          scope,
          sortModel: [],
          filterModel: {},
          filteredUnits: 0,
          totalUnits: 0,
        });
        if (!metadataOf(scope)) {
          loadMetadata(scope);
        }
        if (scope.kind === 'subcategory') {
          loadPerformanceFilters(scope.uuid);
        }
      };

      /**
       * Distinct values for one set-filter column, scoped like the page query: the All-models tab has its
       * own endpoint because a subcategory-scoped one cannot answer for a page that spans subcategories
       * (§6.6). Not an `rxMethod` — AG Grid's set filter wants the values delivered to its own callback.
       */
      const filterValues = (scope: StudyNextScope, field: string): Observable<FilterValuesResponseDto> => {
        const { projectId, studyId } = ids();

        return scope.kind === 'all'
          ? service.getStudyFilterValues(projectId, studyId, field)
          : service.getSubcategoryFilterValues(projectId, studyId, scope.uuid, field);
      };

      /**
       * The scope's performances carrying accessories, for the heading bar's bulk-assignment dialog.
       * Scoped like `filterValues` and, like it, not an `rxMethod` and not state: the list is fetched
       * when the dialog opens and lives as long as the dialog. Whether the icon that opens it exists at
       * all is `metadata.hasPerformanceAccessories`, which is already in state.
       */
      const unitsWithPerformanceAccessories = (
        scope: StudyNextScope
      ): Observable<UnitsWithPerformanceAccessoriesResponseDto> => {
        const { projectId, studyId } = ids();

        return scope.kind === 'all'
          ? service.getStudyUnitsWithPerformanceAccessories(projectId, studyId)
          : service.getSubcategoryUnitsWithPerformanceAccessories(projectId, studyId, scope.uuid);
      };

      /** The study's, not the scope's: no scope guard. Concurrent calls collapse through `switchMap`. */
      const refreshCounters = rxMethod<void>(
        pipe(
          switchMap(() => {
            const { projectId, studyId } = ids();

            return service.getStudyCounters(projectId, studyId).pipe(
              tapResponse({
                next: counters => {
                  patchState(store, { counters });
                  // What changed the counters may have changed the column-filtered counts too.
                  refreshFilteredCounts();
                },
                error: () => patchState(store, { hasError: true }),
              })
            );
          })
        )
      );

      const refreshGoodForSelection = rxMethod<void>(
        pipe(
          switchMap(() => {
            const { projectId, studyId } = ids();

            return service.getStudyGoodForSelection(projectId, studyId).pipe(
              tapResponse({
                next: goodForSelection => patchState(store, { goodForSelectionItems: goodForSelection.items }),
                error: () => patchState(store, { hasError: true }),
              })
            );
          })
        )
      );

      /** Two methods, so that neither cancels the other: a filter change re-reads the counters alone. */
      const refreshScaffold = (): void => {
        refreshCounters();
        refreshGoodForSelection();
      };

      /**
       * Tells the grid to re-query once the metadata, and the detail when asked, have landed: a row read before
       * its working condition's line would miss it. Unlike `loadStudy` or `selectScope`, keeps scope, sort and filter.
       */
      let detailPending = false;
      const invalidateRows = rxMethod<{ withDetail: boolean }>(
        pipe(
          tap(({ withDetail }) => {
            forgetMetadata();
            // A call cancelled by `switchMap` leaves the detail it owed to the call that cancelled it.
            detailPending ||= withDetail;
          }),
          switchMap(() => {
            const scope = store.scope();
            const detail$ = detailPending ? projectStudies.refreshStudy(ids().studyId) : of(null);

            return forkJoin([detail$, fetchMetadata(scope)]).pipe(
              tapResponse({
                next: ([, metadata]) => {
                  detailPending = false;
                  setMetadata(scope, metadata);
                  // On another tab by now: that tab's grid queries for itself.
                  if (sameScope(scope, store.scope())) {
                    store._rowsInvalidated.next();
                  }
                },
                error: () => patchState(store, { hasError: true }),
              })
            );
          })
        )
      );

      /** Everything, after a reconnect: events sent while the socket was down cannot be replayed (`PLAN.md` §6.9). */
      const refreshAll = (): void => {
        refreshScaffold();
        invalidateRows({ withDetail: true });
      };

      /**
       * The user's way out of the error banner: the scope's own reads, again.
       *
       * `hasError` is cleared here rather than on each read's success, and that is the point. A scope whose
       * `metadata` read failed renders no columns, so no `units/search` ever runs — and `queryPage` is the
       * only place that used to clear the flag, which left the banner up with no table under it and no way
       * back. Clearing it on any other read's success instead would take the banner away while the table is
       * still missing. Cleared on the attempt, set again by whatever fails again.
       *
       * Not the same as `loadStudy`: that one resets state and returns the page to All models, and
       * `selectScope` clears sort and filter — `PLAN.md` §6 keeps both across everything that is not a
       * navigation. A retry is not a navigation.
       */
      const retry = (): void => {
        patchState(store, { hasError: false });

        const scope = store.scope();
        refreshScaffold();
        loadMetadata(scope);
        if (scope.kind === 'subcategory') {
          loadPerformanceFilters(scope.uuid);
        }
      };

      /** The detail alone — a rename, a study note: nothing the grid draws rows from. */
      const refreshDetail = rxMethod<void>(
        pipe(
          switchMap(() =>
            projectStudies.refreshStudy(ids().studyId).pipe(
              tapResponse({
                next: () => undefined,
                error: () => patchState(store, { hasError: true }),
              })
            )
          )
        )
      );

      /**
       * One unit as the grid builds its row from it, for an in-place update. Not state and not cached: the
       * row model holds the row, and `ProjectStudiesStore.unitById` is the unit page's cache, not this one.
       */
      const fetchUnit = (unitId: string): Observable<StudyUnitDto> => {
        const { projectId, studyId } = ids();

        return service.getStudyUnit(projectId, studyId, unitId).pipe(map(detail => detail.unit));
      };

      /** Units deleted elsewhere leave the selection, so bulk delete never sends an id the server no longer has. */
      const pruneSelection = (unitIds: string[]): void => {
        const removed = new Set(unitIds);
        patchState(store, { selectedUnitIds: store.selectedUnitIds().filter(unitId => !removed.has(unitId)) });
      };

      /**
       * Realtime events → the re-reads they ask for (`planRefresh`), each through its own coalescing
       * method. The page's own writes arrive here too, echoed by the backend like everyone else's: one
       * refresh channel, whoever wrote. Leaving on `STUDY_DELETED` is the page's business.
       *
       * Events that arrive together are read as one (`PLAN.md` §6.11): a derating change announces itself
       * twice back to back, a bulk operation many times, and re-reading on each event started every read
       * again and cancelled the previous one — which the server had to serve anyway. So the plans are
       * merged once the events have stopped for `STUDY_EVENT_COALESCE_MS`, against the tab active then.
       * Two things do not wait: removed units leave the selection, and held metadata an event outdates is
       * forgotten before a tab can be opened on it.
       */
      const pendingEvents: StudyEventDto[] = [];
      const onStudyEvent = rxMethod<StudyEventDto>(
        pipe(
          tap(event => {
            if (event.type === 'UNITS_REMOVED') {
              pruneSelection(event.unitIds ?? []);
            }
            // Its subcategory's tab and All models, which spans every subcategory; a study-level event, all.
            if (planRefresh(event, store.subcategoryId()).metadata) {
              forgetMetadata(event.subcategoryId ? [event.subcategoryId, scopeKey({ kind: 'all' })] : undefined);
            }
            pendingEvents.push(event);
          }),
          debounceTime(STUDY_EVENT_COALESCE_MS),
          tap(() => {
            const subcategoryId = store.subcategoryId();
            const plan = pendingEvents
              .splice(0)
              .map(event => planRefresh(event, subcategoryId))
              .reduce(mergeRefresh);

            if (plan.counters) {
              refreshCounters();
            }
            if (plan.goodForSelection) {
              refreshGoodForSelection();
            }
            if (plan.rows) {
              invalidateRows({ withDetail: plan.detail });
            } else {
              if (plan.requery) {
                store._rowsInvalidated.next();
              }
              if (plan.detail) {
                refreshDetail();
              }
            }
            if (plan.units.length > 0) {
              store._unitsChanged.next(plan.units);
            }
            if (plan.performanceFilters && subcategoryId) {
              loadPerformanceFilters(subcategoryId);
            }
          })
        )
      );

      return {
        queryPage,
        metadataOf,
        refreshScaffold,
        invalidateRows,
        refreshAll,
        retry,
        loadPerformanceFilters,
        refreshDetail,
        fetchUnit,
        pruneSelection,
        onStudyEvent,
        filterValues,
        unitsWithPerformanceAccessories,
        selectScope,
        selectSubcategory: (subcategoryId: string): void => selectScope({ kind: 'subcategory', uuid: subcategoryId }),
        selectAllModels: (): void => selectScope({ kind: 'all' }),
        /**
         * `initialScope` is which slice the page opens on. The study page opens on All models, as the
         * legacy one does; the interim study page has a single subcategory and no tab strip, so it opens
         * on that — resolved here rather than by the page because the subcategory list is what this load
         * fetches, and a page waiting for it would first fetch, and show, a scope it never wanted.
         */
        loadStudy: rxMethod<{ projectId: string; studyId: string; initialScope?: 'all' | 'first-subcategory' }>(
          pipe(
            tap(({ projectId, studyId }) =>
              patchState(store, {
                ...initialState,
                projectId,
                studyId,
                isLoadingStudy: true,
              })
            ),
            // The detail is usually already held — the route resolver loaded it before this page rendered.
            switchMap(({ studyId, initialScope }) =>
              forkJoin([
                // The scope is chosen as soon as the subcategory list is known — at once when the detail is
                // held — so its metadata, the slowest read and the one the grid's columns wait for, starts
                // beside the counters and good-for-selection rather than after them: they only fill labels.
                projectStudies.ensureStudy(studyId).pipe(
                  tap(detail => {
                    const first = detail.subcategories[0];
                    selectScope(
                      initialScope === 'first-subcategory' && first
                        ? { kind: 'subcategory', uuid: first.uuid }
                        : { kind: 'all' }
                    );
                  })
                ),
                fetchScaffold(),
              ]).pipe(
                tapResponse({
                  next: ([, scaffold]) => {
                    patchState(store, { ...scaffold, isLoadingStudy: false });
                    refreshFilteredCounts();
                  },
                  error: () => patchState(store, { isLoadingStudy: false, hasError: true }),
                })
              )
            )
          )
        ),
        setSelectedUnitIds(selectedUnitIds: string[]): void {
          patchState(store, { selectedUnitIds });
        },
      };
    }
  )
);
